import dayjs from "dayjs"
import { useCallback } from "react"
import AutoSizer from "react-virtualized-auto-sizer"
import { VariableSizeList } from "react-window"

import {
  useGraphContext,
  timeDividerWidth,
} from "./GraphContext"
import styles from "./VirtualList.module.scss"
import { VirtualListItem } from "./VirtualListItem"
import type { ReactWindowAutoSizerArgs } from "src/types"

export function VirtualList() {
  const {
    listRef,
    onResize,
    onScroll,
    pointsPerHour,
    hoursPerGraphItem,
    graphStart,
    graphEnd,
    numberOfItems,
  } = useGraphContext()

  /*
   * the first item is a variable size based on the current time
   * the last item just adds a space so that the last time divider is not cut off
   * all in-between items are the same size
   */
  const getItemSize = useCallback(
    (index: number) => {
      if (index === 0) {
        const startTime = dayjs(graphStart)
          .add(
            (numberOfItems - 2) * hoursPerGraphItem,
            "hours",
          )

        return dayjs(graphEnd)
          .diff(
            startTime,
            "hours",
            true,
          ) * pointsPerHour
      }

      if (index === numberOfItems - 1) {
        return timeDividerWidth / 2
      }

      return hoursPerGraphItem * pointsPerHour
    },
    [
      pointsPerHour,
      graphStart,
      graphEnd,
      hoursPerGraphItem,
      numberOfItems,
    ],
  )

  const getItemKey = useCallback(
    (index: number): string => {
      // keys ordered from oldest to newest
      const reverseIndex = numberOfItems - index
      return dayjs(graphStart)
        .add(
          reverseIndex * hoursPerGraphItem,
          "hours",
        )
        .toISOString()
    },
    [
      numberOfItems,
      hoursPerGraphItem,
      graphStart,
    ],
  )

  return (
    <AutoSizer onResize={onResize}>
      {({
        height,
        width,
      }: ReactWindowAutoSizerArgs) => (
        <VariableSizeList
          className={styles.virtualList}
          height={height}
          width={width}
          ref={listRef}
          itemCount={numberOfItems}
          estimatedItemSize={hoursPerGraphItem * pointsPerHour}
          layout="horizontal"
          direction="rtl"
          overscanCount={1}
          itemSize={getItemSize}
          itemKey={getItemKey}
          onScroll={onScroll}
        >
          {VirtualListItem}
        </VariableSizeList>
      )}
    </AutoSizer>
  )
}
